import React, { useState, useEffect, useCallback } from 'react';
import { useApp } from '../context/AppContext';
import api from '../services/api';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Badge } from '../components/ui/badge';
import { Textarea } from '../components/ui/textarea';
import { Plus, Pencil, Trash2, BookOpen, ChevronRight } from 'lucide-react';

const TIPOS_CUENTA = [
  { value: 'ACTIVO', label: 'Activo', color: 'bg-blue-100 text-blue-800' },
  { value: 'PASIVO', label: 'Pasivo', color: 'bg-red-100 text-red-800' },
  { value: 'PATRIMONIO', label: 'Patrimonio', color: 'bg-purple-100 text-purple-800' },
  { value: 'INGRESO', label: 'Ingreso', color: 'bg-green-100 text-green-800' },
  { value: 'GASTO', label: 'Gasto', color: 'bg-orange-100 text-orange-800' }
];

const SUBTIPOS = {
  ACTIVO: ['CORRIENTE', 'NO_CORRIENTE', 'FIJO', 'CAJA', 'BANCOS', 'EFECTIVO', 'CXC', 'CUENTAS_POR_COBRAR', 'CLIENTES', 'INVENTARIO', 'MERCADERIA', 'EXISTENCIAS'],
  PASIVO: ['CORRIENTE', 'NO_CORRIENTE', 'LARGO_PLAZO', 'CXP', 'CUENTAS_POR_PAGAR', 'PROVEEDORES', 'IGV', 'IMPUESTOS', 'OBLIGACIONES'],
  PATRIMONIO: ['CAPITAL', 'RESERVAS', 'RESULTADOS', 'RESULTADO'],
  INGRESO: ['OPERATIVO', 'NO_OPERATIVO', 'FINANCIERO'],
  GASTO: ['OPERATIVO', 'ADMINISTRATIVO', 'FINANCIERO', 'COSTO']
};

const CuentasContablesPage = () => {
  const { empresaActual } = useApp();
  const [cuentas, setCuentas] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [formData, setFormData] = useState({
    codigo: '',
    nombre: '',
    tipo: 'ACTIVO',
    subtipo: '',
    cuenta_padre_id: '',
    descripcion: '',
    activo: true,
    saldo_inicial: 0
  });

  const loadData = useCallback(async () => {
    if (!empresaActual?.id) return;
    setLoading(true);
    try {
      const response = await api.get(`/cuentas-contables?empresa_id=${empresaActual.id}`);
      setCuentas(response.data);
    } catch (error) {
      toast.error('Error al cargar cuentas');
    } finally {
      setLoading(false);
    }
  }, [empresaActual?.id]);

  useEffect(() => { loadData(); }, [loadData]);

  const handleOpenDialog = (item = null) => {
    if (item) {
      setEditingItem(item);
      setFormData({
        codigo: item.codigo || '',
        nombre: item.nombre || '',
        tipo: item.tipo || 'ACTIVO',
        subtipo: item.subtipo || '',
        cuenta_padre_id: item.cuenta_padre_id?.toString() || '',
        descripcion: item.descripcion || '',
        activo: item.activo !== false,
        saldo_inicial: item.saldo_inicial || 0
      });
    } else {
      setEditingItem(null);
      setFormData({ codigo: '', nombre: '', tipo: 'ACTIVO', subtipo: '', cuenta_padre_id: '', descripcion: '', activo: true, saldo_inicial: 0 });
    }
    setDialogOpen(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.codigo || !formData.nombre) {
      toast.error('Código y nombre son requeridos');
      return;
    }

    const payload = {
      empresa_id: empresaActual.id,
      codigo: formData.codigo,
      nombre: formData.nombre,
      tipo: formData.tipo,
      subtipo: formData.subtipo || null,
      cuenta_padre_id: formData.cuenta_padre_id && formData.cuenta_padre_id !== 'none' ? parseInt(formData.cuenta_padre_id) : null,
      descripcion: formData.descripcion || null,
      activo: formData.activo,
      saldo_inicial: parseFloat(formData.saldo_inicial) || 0
    };

    try {
      if (editingItem) {
        await api.put(`/cuentas-contables/${editingItem.id}`, payload);
        toast.success('Cuenta actualizada');
      } else {
        await api.post('/cuentas-contables', payload);
        toast.success('Cuenta creada');
      }
      setDialogOpen(false);
      loadData();
    } catch (error) {
      toast.error('Error al guardar');
    }
  };

  const handleDelete = async () => {
    if (!deleteConfirm) return;
    try {
      await api.delete(`/cuentas-contables/${deleteConfirm.id}`);
      toast.success('Cuenta eliminada');
      setDeleteConfirm(null);
      loadData();
    } catch (error) {
      toast.error('Error al eliminar. Puede tener cuentas hijas o estar en uso.');
    }
  };

  const getTipoColor = (tipo) => {
    const found = TIPOS_CUENTA.find(t => t.value === tipo);
    return found ? found.color : 'bg-gray-100 text-gray-800';
  };

  const getTipoLabel = (tipo) => {
    const found = TIPOS_CUENTA.find(t => t.value === tipo);
    return found ? found.label : tipo;
  };

  // Organizar cuentas jerárquicamente
  const getCuentasJerarquicas = () => {
    const cuentasPadre = cuentas.filter(c => !c.cuenta_padre_id);
    const getCuentasHijas = (padreId, nivel = 0) => {
      return cuentas
        .filter(c => c.cuenta_padre_id === padreId)
        .map(cuenta => ({
          ...cuenta,
          nivel,
          hijas: getCuentasHijas(cuenta.id, nivel + 1)
        }));
    };
    return cuentasPadre.map(cuenta => ({
      ...cuenta,
      nivel: 0,
      hijas: getCuentasHijas(cuenta.id, 1)
    }));
  };

  // Aplanar para mostrar en tabla
  const flattenCuentas = (cuentasJerarquicas, result = []) => {
    cuentasJerarquicas.forEach(cuenta => {
      result.push(cuenta);
      if (cuenta.hijas?.length > 0) {
        flattenCuentas(cuenta.hijas, result);
      }
    });
    return result;
  };

  const cuentasFlat = flattenCuentas(getCuentasJerarquicas());
  const cuentasPadreOptions = cuentas.filter(c => c.id !== editingItem?.id);

  if (!empresaActual) return <div className="text-center py-12 text-gray-500">Selecciona una empresa</div>;

  return (
    <div className="space-y-6" data-testid="cuentas-contables-page">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <BookOpen className="w-8 h-8 text-indigo-600" />
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Plan de Cuentas</h1>
            <p className="text-sm text-gray-500">Catálogo de cuentas contables</p>
          </div>
        </div>
        <Button onClick={() => handleOpenDialog()} data-testid="new-cuenta-btn">
          <Plus className="w-4 h-4 mr-2" />Nueva Cuenta
        </Button>
      </div>

      {/* Resumen por tipo */}
      <div className="grid grid-cols-5 gap-4">
        {TIPOS_CUENTA.map(tipo => {
          const count = cuentas.filter(c => c.tipo === tipo.value).length;
          return (
            <Card key={tipo.value} className="text-center">
              <CardContent className="pt-4">
                <Badge className={tipo.color}>{tipo.label}</Badge>
                <p className="text-2xl font-bold mt-2">{count}</p>
              </CardContent>
            </Card>
          );
        })}
      </div>

      <Card>
        <CardContent className="p-0">
          {loading ? (
            <div className="flex justify-center py-12">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600"></div>
            </div>
          ) : cuentas.length === 0 ? (
            <div className="text-center py-12 text-gray-500">No hay cuentas registradas</div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-32">Código</TableHead>
                  <TableHead>Nombre</TableHead>
                  <TableHead>Tipo</TableHead>
                  <TableHead>Subtipo</TableHead>
                  <TableHead className="text-right">Saldo Inicial</TableHead>
                  <TableHead className="text-right">Acciones</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {cuentasFlat.map((cuenta) => (
                  <TableRow key={cuenta.id} className={cuenta.nivel > 0 ? 'bg-gray-50' : ''}>
                    <TableCell className="font-mono">
                      <div className="flex items-center">
                        {cuenta.nivel > 0 && (
                          <span style={{ marginLeft: `${cuenta.nivel * 16}px` }} className="text-gray-400 mr-1">
                            <ChevronRight className="w-3 h-3 inline" />
                          </span>
                        )}
                        {cuenta.codigo}
                      </div>
                    </TableCell>
                    <TableCell className={cuenta.nivel === 0 ? 'font-semibold' : ''}>
                      {cuenta.nombre}
                    </TableCell>
                    <TableCell>
                      <Badge className={getTipoColor(cuenta.tipo)}>{getTipoLabel(cuenta.tipo)}</Badge>
                    </TableCell>
                    <TableCell className="text-gray-500">{cuenta.subtipo || '-'}</TableCell>
                    <TableCell className="text-right font-mono">
                      {cuenta.saldo_inicial ? `S/ ${cuenta.saldo_inicial.toLocaleString('es-PE', {minimumFractionDigits: 2})}` : '-'}
                    </TableCell>
                    <TableCell className="text-right">
                      <Button variant="ghost" size="sm" onClick={() => handleOpenDialog(cuenta)}>
                        <Pencil className="w-4 h-4" />
                      </Button>
                      <Button variant="ghost" size="sm" onClick={() => setDeleteConfirm(cuenta)}>
                        <Trash2 className="w-4 h-4 text-red-500" />
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* Dialog Crear/Editar */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>{editingItem ? 'Editar' : 'Nueva'} Cuenta Contable</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Código *</Label>
                <Input 
                  value={formData.codigo} 
                  onChange={(e) => setFormData({ ...formData, codigo: e.target.value })}
                  placeholder="Ej: 1.1.1"
                  required
                />
              </div>
              <div>
                <Label>Tipo *</Label>
                <Select value={formData.tipo} onValueChange={(v) => setFormData({ ...formData, tipo: v, subtipo: '' })}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {TIPOS_CUENTA.map(t => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </div>
            
            <div>
              <Label>Nombre *</Label>
              <Input 
                value={formData.nombre} 
                onChange={(e) => setFormData({ ...formData, nombre: e.target.value })}
                placeholder="Nombre de la cuenta"
                required
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Subtipo</Label>
                <Select value={formData.subtipo || "none"} onValueChange={(v) => setFormData({ ...formData, subtipo: v === "none" ? "" : v })}>
                  <SelectTrigger><SelectValue placeholder="Seleccionar" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">Sin subtipo</SelectItem>
                    {(SUBTIPOS[formData.tipo] || []).map(s => <SelectItem key={s} value={s}>{s.replace('_', ' ')}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Cuenta Padre</Label>
                <Select value={formData.cuenta_padre_id || "none"} onValueChange={(v) => setFormData({ ...formData, cuenta_padre_id: v === "none" ? "" : v })}>
                  <SelectTrigger><SelectValue placeholder="Seleccionar" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">Sin cuenta padre</SelectItem>
                    {cuentasPadreOptions.map(c => <SelectItem key={c.id} value={c.id.toString()}>{c.codigo} - {c.nombre}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div>
              <Label>Descripción</Label>
              <Textarea 
                value={formData.descripcion} 
                onChange={(e) => setFormData({ ...formData, descripcion: e.target.value })}
                placeholder="Descripción opcional"
                rows={2}
              />
            </div>

            {/* Saldo Inicial - solo para cuentas de balance */}
            {['ACTIVO', 'PASIVO', 'PATRIMONIO'].includes(formData.tipo) && (
              <div className="bg-amber-50 p-4 rounded-lg border border-amber-200">
                <Label className="text-amber-800 font-medium">Saldo Inicial (S/)</Label>
                <Input 
                  type="number"
                  step="0.01"
                  value={formData.saldo_inicial} 
                  onChange={(e) => setFormData({ ...formData, saldo_inicial: e.target.value })}
                  placeholder="0.00"
                  className="mt-1"
                />
                <p className="text-xs text-amber-600 mt-1">
                  Este saldo se usa como punto de partida para el Balance General
                </p>
              </div>
            )}

            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>Cancelar</Button>
              <Button type="submit">Guardar</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Dialog Eliminar */}
      <Dialog open={!!deleteConfirm} onOpenChange={() => setDeleteConfirm(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>¿Eliminar cuenta?</DialogTitle>
          </DialogHeader>
          <p className="text-gray-600">
            ¿Estás seguro de eliminar la cuenta <strong>{deleteConfirm?.codigo} - {deleteConfirm?.nombre}</strong>?
          </p>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteConfirm(null)}>Cancelar</Button>
            <Button variant="destructive" onClick={handleDelete}>Eliminar</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default CuentasContablesPage;
